import { useQuery } from '@tanstack/react-query';
import { Shield, AlertTriangle, CheckCircle, DollarSign, Loader2, Users, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_STYLES: Record<string, { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline'; icon: any }> = {
  compliant: { label: 'Compliant', variant: 'default', icon: CheckCircle },
  underpaid: { label: 'Underpaid', variant: 'destructive', icon: AlertTriangle },
  overpaid: { label: 'Overpaid', variant: 'secondary', icon: TrendingDown },
  no_data: { label: 'No Data', variant: 'outline', icon: Shield },
};

export default function SuperCompliancePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['super-compliance'],
    queryFn: async () => { const r = await apiClient.get('/compliance/super'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Calculating superannuation obligations...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const clients = data?.clients || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="Superannuation Compliance"
        description="SG obligation calculations from payroll wages data across all connected clients"
      />


      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
                <div><p className="text-2xl font-bold">{summary.totalClients}</p><p className="text-xs text-muted-foreground">Clients Scanned</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><CheckCircle className="h-5 w-5 text-green-500" /></div>
                <div><p className="text-2xl font-bold">{summary.compliant}</p><p className="text-xs text-muted-foreground">Compliant</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.underpaid > 0 ? 'border-red-500/30' : ''}>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
                <div><p className="text-2xl font-bold">{summary.underpaid}</p><p className="text-xs text-muted-foreground">Underpaid</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.totalShortfall > 0 ? 'border-red-500/30' : ''}>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-amber-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalShortfall)}</p><p className="text-xs text-muted-foreground">Total Shortfall</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client SG Analysis</CardTitle>
          <CardDescription>Superannuation Guarantee obligations per client</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {clients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No connected clients found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Client</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Total Wages</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">SG Required</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">SG Paid</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Shortfall</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c: any) => {
                  const style = STATUS_STYLES[c.status] || STATUS_STYLES.no_data;
                  const StatusIcon = style.icon;
                  return (
                    <tr key={c.clientId} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium">{c.clientName}</span>
                        {c.sgRate > 0 && <span className="text-[10px] text-muted-foreground ml-2">@ {(c.sgRate * 100).toFixed(1)}%</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{c.totalWages > 0 ? fmtCurrency(c.totalWages) : '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{c.totalSuperRequired > 0 ? fmtCurrency(c.totalSuperRequired) : '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{c.totalSuperPaid > 0 ? fmtCurrency(c.totalSuperPaid) : '-'}</td>
                      <td className={cn('px-4 py-3 text-right font-mono font-medium', c.shortfall > 0 ? 'text-red-500' : c.shortfall < 0 ? 'text-green-500' : '')}>
                        {c.shortfall !== 0 ? (c.shortfall > 0 ? '-' : '+') + fmtCurrency(c.shortfall) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={style.variant} className="text-[10px] gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {style.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
